/**
 * @file Barrière de chargement d'une page composée de widgets autonomes.
 * @module packages.common.components.loading-screen.page-boot
 *
 * Doctrine du panel : une page n'apparaît jamais en morceaux. Quand une vue
 * est composée de widgets qui chargent chacun leurs données (stats, tableaux,
 * graphiques), PageBoot affiche UN ContentLoader tant que tous les widgets
 * n'ont pas fini leur premier chargement, puis révèle la page entière d'un
 * bloc.
 *
 * Fonctionnement : chaque widget déclare son état via usePageBootGate(
 * isLoading) — une ligne. Le contenu est monté dès le départ mais masqué
 * (visibility: hidden, jamais display: none : les hooks SWR fetchent et les
 * graphiques peuvent mesurer leur conteneur). La barrière est à sens unique :
 * une fois la page révélée, les chargements suivants (pagination, filtres)
 * ne la re-masquent pas — ils restent contextuels au widget.
 */

import {createContext, useCallback, useContext, useEffect, useId, useMemo, useReducer, useRef, useState} from 'react';
import PropTypes from 'prop-types';
// @mui
import Box from '@mui/material/Box';
import ContentLoader from './content-loader';

const PageBootContext = createContext(null);

/**
 * Déclare l'état de chargement d'un widget auprès du PageBoot englobant.
 * Sans PageBoot au-dessus, le hook est neutre (le widget reste utilisable
 * partout).
 *
 * @param {boolean} isLoading - Premier chargement du widget en cours
 * @return {void}
 */
export function usePageBootGate(isLoading) {
  const ctx = useContext(PageBootContext);
  const id = useId();

  useEffect(() => {
    if (!ctx) return undefined;
    ctx.report(id, Boolean(isLoading));
    return undefined;
  }, [ctx, id, isLoading]);

  useEffect(() => {
    if (!ctx) return undefined;
    return () => ctx.unregister(id);
  }, [ctx, id]);
}

/**
 * Variante composant de usePageBootGate, pour les vues dont les hooks de
 * données vivent dans le composant qui rend le PageBoot lui-même (le hook
 * doit être appelé SOUS le provider) : <PageBootGate loading={isLoading} />.
 *
 * @param {Object} props - Props du composant
 * @param {boolean} props.loading - Premier chargement en cours
 * @return {null} Aucun rendu
 */
export function PageBootGate({loading}) {
  usePageBootGate(loading);
  return null;
}

PageBootGate.propTypes = {
  loading: PropTypes.bool,
};

/**
 * Barrière de chargement : loader unique tant qu'un widget déclaré charge,
 * puis révélation de la page complète en une fois.
 *
 * @param {Object} props - Props du composant
 * @param {Object} props.children - Contenu de la page (widgets déclarants)
 * @param {Object} [props.placeholder] - Visuel affiché pendant le boot
 *   (défaut : ContentLoader ; passer un skeleton propre à la page pour un
 *   chargement sur mesure sans toucher au mécanisme)
 * @param {*} [props.resetKey] - Quand cette valeur change (ex. compte
 *   sélectionné), la barrière se ré-arme : la page rejoue un boot complet au
 *   lieu de se vider par morceaux
 * @param {Object} [props.sx] - Styles MUI supplémentaires du conteneur révélé
 * @param {boolean} [props.fullHeight=false] - La page gère sa propre hauteur et
 *   son défilement interne (cf. `useFullHeightLayout`). Les deux enveloppes de
 *   cette barrière prennent alors toute la hauteur disponible : sans cela elles
 *   retombent en hauteur automatique, la chaîne `height: 100%` de la page est
 *   rompue, et le contenu trop grand est coupé par le `overflow: hidden` du
 *   layout — sans barre de défilement nulle part.
 * @return {JSX.Element} La page gatée
 */
export default function PageBoot({children, placeholder, resetKey, sx, fullHeight = false}) {
  const reportsRef = useRef(new Map());
  const [booted, setBooted] = useState(false);
  const [, forceCheck] = useReducer((tick) => tick + 1, 0);

  // Ré-armement pendant le rendu (pattern React « adjusting state on props
  // change ») : le changement de contexte rejoue le boot avant tout commit
  const prevResetKeyRef = useRef(resetKey);
  if (prevResetKeyRef.current !== resetKey) {
    prevResetKeyRef.current = resetKey;
    if (booted) setBooted(false);
  }

  const report = useCallback((id, loading) => {
    reportsRef.current.set(id, loading);
    forceCheck();
  }, []);

  const unregister = useCallback((id) => {
    reportsRef.current.delete(id);
    forceCheck();
  }, []);

  // Après chaque commit : révèle la page quand plus aucun widget ne charge.
  // Les effets des enfants s'exécutent avant celui-ci : au premier commit,
  // tous les widgets montés ont déjà déclaré leur état. Volontairement sans
  // tableau de dépendances (la Map est une ref, invisible pour React) ; la
  // garde `booted` + le setState à sens unique empêchent toute boucle.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (booted) return;
    const anyLoading = [...reportsRef.current.values()].some(Boolean);
    if (!anyLoading) setBooted(true);
  });

  const contextValue = useMemo(() => ({report, unregister}), [report, unregister]);

  return (
    <PageBootContext.Provider value={contextValue}>
      {/* `flex` plutôt qu'une hauteur de 100 % : ce conteneur est un enfant de
          la colonne du layout, qui porte aussi la bannière d'abonnement. Une
          hauteur absolue déborderait par-dessus. */}
      <Box sx={{position: 'relative', width: '100%', ...(fullHeight && {flex: '1 1 auto', minHeight: 0})}}>
        {!booted && (placeholder || <ContentLoader />)}
        <Box
          sx={{
            ...(fullHeight && {height: '100%', minHeight: 0}),
            ...(booted ?
              {} :
              {
                // Monté mais invisible : les fetches partent, les graphiques
                // mesurent leur largeur réelle, et rien ne pousse le layout
                visibility: 'hidden',
                position: 'absolute',
                inset: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
              }),
            ...sx,
          }}
        >
          {children}
        </Box>
      </Box>
    </PageBootContext.Provider>
  );
}

PageBoot.propTypes = {
  children: PropTypes.node,
  fullHeight: PropTypes.bool,
  placeholder: PropTypes.node,
  // eslint-disable-next-line react/forbid-prop-types
  resetKey: PropTypes.any,
  sx: PropTypes.object,
};
